import { Page } from '@app/components/page/Page';
import { VideoPlaybackProvider } from '@app/core/playback/video/VideoPlaybackProvider';
import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { StationLivePlayer } from '@app/features/global-live-player/components/StationLivePlayer';
import { useLivePlayerSettingsStore } from '@app/features/global-live-player/store/live-player-settings.store';
import { useStationLiveHlsUrl } from '@app/modules/logger/screens/stream/modes/video/useStationLiveHlsUrl';
import { useQueryParam } from '@mi-front/core';
import { Box } from '@mui/material';
import { useEffect } from 'react';
import { LivePlayerScreenHeader } from '../components/LivePlayerScreenHeader';

/**
 * Full-screen live player for a station. Rendered at /live-player/:station.
 * Syncs store with URL station so clock, fullscreen, and mute controls work.
 * Reuses VideoPlaybackProvider, StationLivePlayer, and header controls from the feature.
 */
export function LivePlayerPage() {
  const station = useActiveStation();
  const setStationId = useLivePlayerSettingsStore((state) => state.setStationId);
  const setStationClock = useLivePlayerSettingsStore((state) => state.setStationClock);

  // Keep store in sync with URL so StationClock, fullscreen, mute work
  useEffect(() => {
    setStationId(station.id);
  }, [station.id]);

  const src = useStationLiveHlsUrl(station.id);
  const autoplay = useQueryParam('autoplay');

  if (!src) {
    return null;
  }

  return (
    <Page htmlTitle={`Live player - ${station.name}`}>
      <VideoPlaybackProvider
        playbackId="live-player"
        exclusivePlaybackMode="muted"
        src={src}
        key={src}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            overflow: 'hidden',
          }}
        >
          <LivePlayerScreenHeader station={station} />
          <StationLivePlayer
            stationId={station.id}
            onClockUpdate={setStationClock}
            options={{
              autoplay: autoplay === 'true',
              muted: false,
              fluid: false,
              fill: true,
            }}
          />
        </Box>
      </VideoPlaybackProvider>
    </Page>
  );
}
